import { R4 } from "@ahryman40k/ts-fhir-types";
import { TokenResponse } from "@openid/appauth";
import { Mutex } from "async-mutex";
import dayjs from "dayjs";
import Vue from "vue";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";

import { fetchPrincipals } from "@/api/professional";
import { makeUuidUrnReference } from "@/components/vaccinations/utils";
import config from "@/config";
import {
  Assistant,
  IDToken,
  IPatient,
  IPerson,
  IdentityProvider,
  JWT,
  Representative,
  Role,
  UnauthorizedError,
  isPatientMode,
  isProfessionalMode,
} from "@/global";
import { base64UrlDecodeUnicode, getPreferredName } from "@/utils";

import AppAuth from "../../appauth";
import Store from "../index";

export enum LocalStorageItemKeys {
  AccessToken = "phellow:oauth2:accessToken",
  TokenType = "phellow:oauth2:accessToken:type",
  ExpiresAt = "phellow:oauth2:accessToken:expiresAt",
  RefreshToken = "phellow:oauth2:refreshToken",
  IDToken = "phellow:oauth2:idToken",
  RedirectPath = "phellow:router:redirect",
  SelectedPatient = "phellow:state:selectedPatient",
  SelectedPatientSPID = "phellow:state:spid",
  EmergencyAccess = "phellow:state:emergencyAccess",
  Role = "phellow:state:role",
  PractitionerGLN = "phellow:state:proxy:gln",
  ProxyPerson = "phellow:state:proxy:person",
  DocumentCount = "phellow:document:count",
  SelectedIdP = "phellow:state:selectedIdP",
}

export enum XuaErrorCodes {
  GLN_NOT_REGISTERED = "gln_not_registered",
}

// TODO: remove
export async function resolveLocalIdentifier(
  accessTokenType: string,
  accessToken: string,
  targetSystem: string
): Promise<{ value: string; system: string }> {
  const url = config.r4.patients + "?_format=json&_elements=identifier&identifier=me_";

  const response = await window.fetch(url, {
    method: "GET",
    headers: {
      Authorization: accessTokenType + " " + accessToken,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to resolve local ID to SPID.");
  }

  const body = (await response.json()) as R4.IBundle;
  const patients = (body.entry ?? []).map((entry) => entry.resource as R4.IPatient);

  if (patients.length <= 0) {
    throw new Error("Failed to resolve local ID to SPID.");
  }
  const identifier = patients[0].identifier?.filter(
    (identifier) => identifier?.system == targetSystem
  );

  if (!identifier || identifier.length < 1) {
    throw new Error("Failed to resolve local ID to SPID.");
  }

  return { value: identifier[0].value!!, system: identifier[0].system!! };
}

@Module({
  dynamic: true,
  store: Store,
  name: "account",
  namespaced: true,
})
export default class AccountModule extends VuexModule {
  accessToken: string | null = window.localStorage.getItem(LocalStorageItemKeys.AccessToken);
  tokenType: string | null = window.localStorage.getItem(LocalStorageItemKeys.TokenType);
  expiresAt: string | null = window.localStorage.getItem(LocalStorageItemKeys.ExpiresAt);
  refreshToken: string | null = window.localStorage.getItem(LocalStorageItemKeys.RefreshToken);
  idToken: string | null = window.localStorage.getItem(LocalStorageItemKeys.IDToken);
  redirectPath: string | null = window.sessionStorage.getItem(LocalStorageItemKeys.RedirectPath);
  selectedIdP: IdentityProvider = window.localStorage.getItem(
    LocalStorageItemKeys.SelectedIdP
  ) as IdentityProvider;

  proxies: (Representative | Assistant)[] | null = null;
  proxiesAreLoading: boolean = false;

  representatives: R4.IRelatedPerson[] | null = null;
  representativesAreLoading: boolean = false;

  idleTimeout: dayjs.Dayjs | null = null;

  mutex: Mutex = new Mutex();

  /** The SPID of the currently logged in user (if the user is a patient and has an EPD)  */
  userSPID: string | null = null;

  sessionPoller: NodeJS.Timeout | null = null;

  xuaTokenType: string | null = null;
  xuaToken: string | null = null;
  xuaTokenExpiresAt: string | null = null;

  /** The person one wants access to. Set to null, when in the Patient role on ones own record. */
  selectedPatient: IPatient | null = this.loadSelectedPatient();
  /** The spid of the selected patient  */
  patientSPID: string | null = window.localStorage.getItem(
    LocalStorageItemKeys.SelectedPatientSPID
  );
  /** The GLN of the person one wants to be assistant to. Only relevant when HCP wants ASS access. */
  practitionerGLN: string | null = window.localStorage.getItem(
    LocalStorageItemKeys.PractitionerGLN
  );
  /** Keep track if HCP is in emergency access mode */
  hasEmergencyAccess: boolean = this.loadEmergencyAccess();
  emergencyAccessRequested: boolean = false;

  // The selected proxy person. Either Assistant or Representative
  proxyPerson: IPerson | null = this.loadSelectedProxyPerson();

  // The role the currently logged in user has
  role: Role =
    (window.localStorage.getItem(LocalStorageItemKeys.Role) as Role) || config.role || Role.None;

  loginIsLoading: boolean = false;

  captchaToken: string | null = null;

  // IF a user (especially a HCP) has no access to the selected EPD
  noAccessToThisEPD: boolean = false;

  loadSelectedPatient(): IPatient | null {
    const selectedPatientString = window.localStorage.getItem(LocalStorageItemKeys.SelectedPatient);
    if (selectedPatientString) {
      const parsedPatient = JSON.parse(selectedPatientString) as IPatient | undefined;
      if (parsedPatient) {
        if (parsedPatient.dateOfBirth) {
          parsedPatient.dateOfBirth = dayjs(parsedPatient.dateOfBirth);
          return parsedPatient;
        }
      }
      return null;
    }
    return null;
  }

  loadEmergencyAccess(): boolean {
    const loadedAccess = window.localStorage.getItem(LocalStorageItemKeys.EmergencyAccess);
    if (loadedAccess) {
      return JSON.parse(loadedAccess) === true;
    }
    return false;
  }

  loadSelectedProxyPerson(): IPerson | null {
    const selectedProxyString = window.localStorage.getItem(LocalStorageItemKeys.ProxyPerson);
    if (selectedProxyString) {
      const parsedProxy = JSON.parse(selectedProxyString) as IPerson | undefined;
      return parsedProxy || null;
    }
    return null;
  }

  @Action({ rawError: true })
  async getAuthenticationHeader(endpointNeedsIDP: boolean = false): Promise<string> {
    return this.mutex.runExclusive(async () => {
      return this.context.dispatch("_getAuthenticationHeader", endpointNeedsIDP) as Promise<string>;
    });
  }

  @Action({ rawError: true })
  async _getAuthenticationHeader(endpointNeedsIDP: boolean = false): Promise<string> {
    let headers = new Headers();

    // We always need a valid ID Token
    if (this.tokenType && this.accessToken && this.expiresAt) {
      if (this.refreshToken) {
        const tokenResponse = await AppAuth(this.selectedIdP!).refresh(this.refreshToken);
        this.setIDPToken(tokenResponse);
      }
    } else {
      throw new Error("One or more IDP token fields are null.");
    }

    if (endpointNeedsIDP) {
      // IDP Token required
      return this.tokenType + " " + this.accessToken;
    } else {
      // Default to XUA Token
      if (this.xuaToken && this.xuaTokenExpiresAt && !this.emergencyAccessRequested) {
        const now = dayjs().add(5, "second");
        const expiration = dayjs(this.xuaTokenExpiresAt);

        if (now.isBefore(expiration)) {
          return this.xuaTokenType + " " + this.xuaToken;
        } else {
          // Fetch new XUA token below
        }
      } else {
        // Fetch new XUA token below
      }

      const url = config.openid[this.selectedIdP]!!.tokenEndpoint;
      const urlParams = new URLSearchParams();
      urlParams.append("client_id", config.openid[this.selectedIdP]!!.clientId);
      urlParams.append("grant_type", "urn:ietf:params:oauth:grant-type:token-exchange");
      urlParams.append("subject_token", this.accessToken!);
      urlParams.append("subject_token_type", "urn:ietf:params:oauth:token-type:access_token");
      urlParams.append("home_community_id", "urn:oid:" + config.homeCommunity.id);

      // Fetch new XUA token here
      // Build URL according to flow chart
      switch (this.role) {
        case Role.HealthcareProfessional:
        case Role.Assistant:
          // SPID necessary for all
          if (this.patientSPID) {
            urlParams.append("resource_id", this.patientSPID!);
          } else {
            throw new Error("No SPID defined.");
          }

          if (this.practitionerGLN && this.proxyPerson) {
            // HCP wants access to PAT as ASS
            urlParams.append("role", Role.Assistant);
            urlParams.append("principal_id", this.practitionerGLN);
            urlParams.append("principal_name", this.proxyPerson.name);
          } else {
            // HCP wants access to PAT
            urlParams.append("role", Role.HealthcareProfessional);
          }

          // Emergency Access
          if (this.emergencyAccessRequested || this.hasEmergencyAccess) {
            urlParams.append("purpose_of_use", "EMER");

            if (this.captchaToken) {
              headers = new Headers([["Captcha-Token", this.captchaToken]]);
            }
          } else {
            urlParams.append("purpose_of_use", "NORM");
          }

          break;
        case Role.Patient:
        case Role.Representative:
          urlParams.append("purpose_of_use", "NORM");
          if (this.patientSPID) {
            // PAT wants REP Access
            urlParams.append("role", Role.Representative);
            urlParams.append("resource_id", this.patientSPID!);
          } else {
            // PAT wants their own XUA token
            urlParams.append("role", Role.Patient);

            if (!this.userSPID) {
              await this.loadUserSPID();
            }
            urlParams.append("resource_id", this.userSPID!);
          }
          break;
        case Role.DocumentAdministrator:
        case Role.PolicyAdministrator:
          urlParams.append("resource_id", this.patientSPID!);
          urlParams.append("role", this.role);
          urlParams.append("purpose_of_use", "NORM");
          break;
        default:
          throw new Error("Unknown role: " + this.role);
      }

      // Start API call
      const encodedURL = encodeURI(url);
      const response = await window.fetch(encodedURL, {
        method: "POST",
        body: urlParams,
        headers,
      });

      if (!response.ok) {
        if (isProfessionalMode(this.role)) {
          switch (response.status) {
            case 400:
              throw new UnauthorizedError("toasts.auth.unauthorizedPatientAccess");
            case 403:
              if ((await response.json()).error == XuaErrorCodes.GLN_NOT_REGISTERED) {
                throw new UnauthorizedError(
                  "toasts.auth.glnNotRegistered",
                  XuaErrorCodes.GLN_NOT_REGISTERED
                );
              } else {
                throw new UnauthorizedError("toasts.auth.unauthorizedPatientAccess");
              }
          }
        }
        throw new Error("XUA Token Response error");
      }

      const tokenResponse = (await response.json()) as any;

      const expirationDate: string = dayjs().add(tokenResponse.expires_in, "second").format();

      this.setXUATokenExpiresAt(expirationDate);
      this.setXUAToken(tokenResponse.access_token);
      this.setXUATokenType(tokenResponse.token_type);

      return this.xuaTokenType + " " + this.xuaToken;
    }
  }

  get parsedIDToken(): IDToken | null {
    if (this.idToken) {
      const parts = this.idToken.split(".");
      if (parts.length === 3) {
        const decoded = base64UrlDecodeUnicode(parts[1]);
        return JSON.parse(decoded) as IDToken;
      }
    }

    return null;
  }

  get subject(): string | null {
    return this.parsedIDToken?.sub || null;
  }

  get loggedInUserRoles(): string[] {
    if (JSON.parse(config.debug.disableRoleCheck) === true) {
      return [Role.Patient, Role.Representative, Role.HealthcareProfessional, Role.Assistant];
    }

    return this.parsedIDToken?.roles || [];
  }

  @Action
  async canReuseSession(): Promise<boolean> {
    if (this.accessToken && this.tokenType && this.idToken && this.expiresAt) {
      const response = await fetch(config.openid[this.selectedIdP]!.sessionEndpoint, {
        method: "GET",
        credentials: "include",
      });

      return response.status == 200;
    }

    return false;
  }

  @Mutation
  setRedirectPath(path: string) {
    window.sessionStorage.setItem(LocalStorageItemKeys.RedirectPath, path);
    this.redirectPath = path;
  }

  @Mutation
  clearRedirectPath() {
    window.sessionStorage.removeItem(LocalStorageItemKeys.RedirectPath);
    this.redirectPath = null;
  }

  @Mutation
  setLoading(loading: boolean) {
    this.loginIsLoading = loading;
  }

  @Mutation
  setIDPToken(tokenResponse: TokenResponse) {
    const expirationDate: string = dayjs().add(tokenResponse.expiresIn!, "second").format();

    window.localStorage.setItem(LocalStorageItemKeys.AccessToken, tokenResponse.accessToken);
    window.localStorage.setItem(LocalStorageItemKeys.TokenType, tokenResponse.tokenType);
    window.localStorage.setItem(LocalStorageItemKeys.ExpiresAt, expirationDate);
    tokenResponse.refreshToken &&
      window.localStorage.setItem(LocalStorageItemKeys.RefreshToken, tokenResponse.refreshToken);

    if (tokenResponse.idToken) {
      window.localStorage.setItem(LocalStorageItemKeys.IDToken, tokenResponse.idToken);
      this.idToken = tokenResponse.idToken;
    }

    this.accessToken = tokenResponse.accessToken;
    this.tokenType = tokenResponse.tokenType;
    this.expiresAt = expirationDate;
    this.refreshToken = tokenResponse.refreshToken || null;
  }

  @Mutation
  setProxies(proxies: (Representative | Assistant)[] | null) {
    this.proxies = proxies;
  }

  @Mutation
  setProxiesLoading(loading: boolean) {
    this.proxiesAreLoading = loading;
  }

  @Action({ rawError: true })
  async loadUserSPID() {
    if (config.debug.staticSPID && this.parsedIDToken?.sub) {
      const mappedSPID = JSON.parse(config.debug.staticSPID);
      const staticSPID = mappedSPID[this.parsedIDToken.sub];
      this.setUserSPID(staticSPID);
    } else {
      this.setUserSPID(this.parsedIDToken?.spid || null);
    }
  }

  @Action
  async updateSessionTimeout() {
    const response = await fetch(config.openid[this.selectedIdP]!!.sessionEndpoint, {
      method: "GET",
      credentials: "include",
    });

    if (response.status == 401) {
      window.dispatchEvent(new CustomEvent("phellow:event:logout"));
      return;
    }

    const body = await response.json();
    this.resetIdleTimeout(dayjs(body.expiresAt));
  }

  @Action
  startSessionPoller() {
    if (!this.sessionPoller)
      this.setSessionPoller(
        setInterval(async () => {
          await this.updateSessionTimeout();
        }, 5000)
      );
  }

  @Action({ rawError: true })
  async fetchRepresentatives() {
    try {
      // TODO: Query representatives relative to the selected patient
      if (this.isRepresentativeWithSelectedProxy) {
        this.setRepresentatives({ resourceType: "List", entry: [] });
        return;
      }

      this.setRepresentativesLoading(true);
      const authHeader = await this.getAuthenticationHeader(true);
      if (authHeader == null) {
        return;
      }

      const response = await fetch(encodeURI(config.eprServices.relatedPerson), {
        method: "GET",
        headers: {
          Authorization: authHeader,
        },
      });

      const representatives = await response.json();
      this.setRepresentatives(representatives);
      this.setRepresentativesLoading(false);
    } catch (e) {
      this.setRepresentativesLoading(false);
      throw e;
    }
  }

  @Action({ rawError: true })
  async fetchProxies(options?: { autoRepresent: boolean }) {
    this.setProxiesLoading(true);
    let url = "";
    let failureMessage = "";
    switch (this.role) {
      case Role.Patient:
      case Role.Representative:
        url = config.eprServices.representedPerson;
        failureMessage = "navigation.proxies.fetchRepresentativesFailed";
        break;
      case Role.HealthcareProfessional:
      case Role.Assistant:
        url = config.eprServices.assistedPerson;
        failureMessage = "navigation.proxies.fetchAssistantsFailed";
        break;
      default:
        this.setProxies(null);
        this.setProxiesLoading(false);
        return;
    }

    const authHeader = await this.getAuthenticationHeader(true);
    if (authHeader == null) {
      this.setProxiesLoading(false);
      return;
    }

    const response = await window.fetch(encodeURI(url), {
      method: "GET",
      headers: {
        Authorization: authHeader,
        "Content-Type": "application/json; charset=utf-8",
      },
    });

    if (response.ok) {
      let fetchedProxies = (await response.json()) as (Representative | Assistant)[];

      const roles = this.parsedIDToken?.roles;

      // Fetch principals from HPD for unregistered assistants
      if (fetchedProxies.length === 0 && roles?.includes(Role.Assistant)) {
        fetchedProxies = await fetchPrincipals(
          { gln: this.parsedIDToken?.gln, email: this.parsedIDToken?.email },
          authHeader
        );
      }

      // set default proxy if Assistant of only one HealthcareProfessional or Representative of only one Patient
      if (
        options?.autoRepresent &&
        roles?.length === 1 &&
        [Role.Assistant, Role.Representative].includes(roles[0] as Role) &&
        fetchedProxies.length === 1
      ) {
        await this.selectProxy(fetchedProxies[0]);
      }

      this.setProxies(fetchedProxies);
      this.setProxiesLoading(false);
    } else {
      this.setProxies(null);
      this.setProxiesLoading(false);
      return failureMessage;
    }
  }

  // person: Representative | Assistant
  @Action({ rawError: true })
  async selectProxy(person: any) {
    const proxyPerson: IPerson = {
      name: `${person.firstName} ${person.lastName}`,
    };
    this.setProxyPerson(proxyPerson);

    if (person.gln) {
      this.setPractitionerGLN(person.gln);
      this.setRole(Role.Assistant);
      return;
    }

    this.clearXUAToken();
    this.setPatientSPID(person.spid);

    try {
      await this.getAuthenticationHeader();
      this.setRole(Role.Representative);
    } catch (error) {
      this.clearXUAToken();
      this.clearProxyPerson();
      throw new UnauthorizedError();
    }
  }

  @Action({ rawError: true })
  async refresh() {
    if (this.refreshToken) {
      const tokenResponse = await AppAuth(this.selectedIdP!).refresh(this.refreshToken);
      this.setIDPToken(tokenResponse);
    }
  }

  @Action({ rawError: true })
  logOutPatient() {
    this.clearXUAToken();
    this.clearPatientSPID();
    this.clearSelectedPatient();
    this.setEmergencyAccessRequested(false);
    this.setHasEmergencyAccess(false);
    this.context.commit("hcprovider/clearSessionHCPs", null, { root: true });
    this.context.commit("document/clearDocuments", null, { root: true });
    this.context.commit("vaccination/clearDocuments", null, { root: true });
    this.context.commit("consent/clearConsent", null, { root: true });
  }

  @Mutation
  setRepresentatives(relatedPersonList: R4.IList) {
    this.representatives =
      relatedPersonList.entry?.map((entry) => {
        return relatedPersonList.contained!.find(
          (relatedPerson) => relatedPerson.id === entry.item.reference
        ) as R4.IRelatedPerson;
      }) || [];
  }

  @Mutation
  setRepresentativesLoading(loading: boolean) {
    this.representativesAreLoading = loading;
  }

  @Mutation
  setSessionPoller(timeout: NodeJS.Timeout) {
    this.sessionPoller = timeout;
  }

  @Mutation
  clearIDPToken() {
    window.localStorage.removeItem(LocalStorageItemKeys.AccessToken);
    window.localStorage.removeItem(LocalStorageItemKeys.TokenType);
    window.localStorage.removeItem(LocalStorageItemKeys.ExpiresAt);
    window.localStorage.removeItem(LocalStorageItemKeys.RefreshToken);

    this.accessToken = null;
    this.tokenType = null;
    this.expiresAt = null;
    this.refreshToken = null;
  }

  @Mutation
  resetIdleTimeout(time: dayjs.Dayjs) {
    this.idleTimeout = time;
  }

  @Mutation
  setIdleTimeout(timeout: dayjs.Dayjs) {
    this.idleTimeout = timeout;
  }

  @Mutation
  setUserSPID(spid: string | null) {
    this.userSPID = spid;
  }

  @Mutation
  clearUserSPID() {
    this.userSPID = null;
  }

  @Mutation
  clearXUAToken() {
    this.xuaToken = null;
    this.xuaTokenExpiresAt = null;
  }

  @Mutation
  setXUAToken(token: string | null) {
    this.xuaToken = token;
  }

  @Mutation
  setXUATokenType(token: string | null) {
    this.xuaTokenType = token;
  }

  @Mutation
  setXUATokenExpiresAt(expiresAt: string | null) {
    this.xuaTokenExpiresAt = expiresAt; // format example: 2020-02-19T12:23:30.67Z
  }

  @Mutation
  setEmergencyAccessRequested(requested: boolean) {
    this.emergencyAccessRequested = requested;
  }

  @Mutation
  setHasEmergencyAccess(bool: boolean) {
    this.hasEmergencyAccess = bool;
    window.localStorage.setItem(LocalStorageItemKeys.EmergencyAccess, JSON.stringify(bool));
  }

  @Mutation
  setRole(role: Role) {
    this.role = role;
    window.localStorage.setItem(LocalStorageItemKeys.Role, role);
  }

  @Mutation
  setSelectedPatient(patient: IPatient) {
    this.selectedPatient = patient;
    window.localStorage.setItem(LocalStorageItemKeys.SelectedPatient, JSON.stringify(patient));
  }

  @Mutation
  clearSelectedPatient() {
    this.selectedPatient = null;
    window.localStorage.removeItem(LocalStorageItemKeys.SelectedPatient);
  }

  @Mutation
  setPatientSPID(spid: string) {
    this.patientSPID = spid;
    window.localStorage.setItem(LocalStorageItemKeys.SelectedPatientSPID, spid);
  }

  @Mutation
  clearPatientSPID() {
    this.patientSPID = null;
    window.localStorage.removeItem(LocalStorageItemKeys.SelectedPatientSPID);
  }

  @Mutation
  setProxyPerson(person: IPerson) {
    this.proxyPerson = person;
    window.localStorage.setItem(LocalStorageItemKeys.ProxyPerson, JSON.stringify(person));
  }

  @Mutation
  clearProxyPerson() {
    this.proxyPerson = null;
    window.localStorage.removeItem(LocalStorageItemKeys.ProxyPerson);
  }

  @Mutation
  setPractitionerGLN(gln: string) {
    this.practitionerGLN = gln;
    window.localStorage.setItem(LocalStorageItemKeys.PractitionerGLN, gln);
  }

  @Mutation
  clearPractitionerGLN() {
    this.practitionerGLN = null;
    window.localStorage.removeItem(LocalStorageItemKeys.PractitionerGLN);
  }

  @Mutation
  setCaptchaToken(token: string) {
    this.captchaToken = token;
  }

  @Mutation
  clearCaptchaToken() {
    this.captchaToken = null;
  }

  @Mutation
  setNoAccessToThisEPD(noAccess: boolean) {
    this.noAccessToThisEPD = noAccess;
  }

  @Mutation
  clearNoAccessToThisEPD() {
    this.noAccessToThisEPD = false;
  }

  @Action({ rawError: true })
  startAuthFlow(provider: IdentityProvider) {
    window.localStorage.setItem(LocalStorageItemKeys.SelectedIdP, provider);
    AppAuth(provider).authorizationRequest();
  }

  @Action({ rawError: true })
  async completeIfPossible() {
    // this.setLoading(true);
    return AppAuth(this.selectedIdP!)
      .completeIfPossible()
      .then((response) => {
        if (response) {
          this.setIDPToken(response);
        }
        return response;
      });
  }

  @Mutation
  logout() {
    window.localStorage.removeItem(LocalStorageItemKeys.AccessToken);
    window.localStorage.removeItem(LocalStorageItemKeys.TokenType);
    window.localStorage.removeItem(LocalStorageItemKeys.ExpiresAt);
    window.localStorage.removeItem(LocalStorageItemKeys.RefreshToken);
    window.localStorage.removeItem(LocalStorageItemKeys.IDToken);
    window.localStorage.removeItem(LocalStorageItemKeys.SelectedPatient);
    window.localStorage.removeItem(LocalStorageItemKeys.SelectedPatientSPID);
    window.localStorage.removeItem(LocalStorageItemKeys.ProxyPerson);
    window.localStorage.removeItem(LocalStorageItemKeys.PractitionerGLN);
    window.localStorage.removeItem(LocalStorageItemKeys.Role);
    window.localStorage.removeItem(LocalStorageItemKeys.EmergencyAccess);
    window.localStorage.removeItem(LocalStorageItemKeys.EmergencyAccess);
    window.localStorage.removeItem(LocalStorageItemKeys.RedirectPath);
    window.localStorage.removeItem(LocalStorageItemKeys.DocumentCount);

    this.userSPID = null;
    this.accessToken = null;
    this.tokenType = null;
    this.expiresAt = null;
    this.refreshToken = null;
    this.idToken = null;
    this.role = config.role;
    this.xuaTokenType = null;
    this.xuaToken = null;
    this.xuaTokenExpiresAt = null;
    this.selectedPatient = null;
    this.patientSPID = null;
    this.proxyPerson = null;
    this.practitionerGLN = null;
    this.hasEmergencyAccess = false;

    if (this.sessionPoller) clearInterval(this.sessionPoller!);

    Vue.notify({ clean: true });
  }

  @Action({ rawError: true })
  async dispatchLogoutEvent() {
    window.dispatchEvent(new CustomEvent("phellow:event:logout"));
  }

  /**
   * checks whether the logged in user may access this portal.
   * User of role PAT musn't enter the Professional Portal for example.
   *
   * @readonly
   * @type {boolean}
   * @memberof AccountModule
   */
  public get idpTokenMatchesConfiguredRole(): boolean {
    const roles = this.loggedInUserRoles;
    if (isProfessionalMode(this.role)) {
      return (
        roles.includes(Role.HealthcareProfessional) ||
        roles.includes(Role.Assistant) ||
        roles.includes(Role.DocumentAdministrator) ||
        roles.includes(Role.PolicyAdministrator)
      );
    } else if (isPatientMode(this.role)) {
      return roles.includes(Role.Patient) || roles.includes(Role.Representative);
    }
    return false;
  }

  public get hasValidToken(): boolean {
    if (this.accessToken == null || this.tokenType == null || this.expiresAt == null) {
      return false;
    }

    if (this.idleTimeout != null) dayjs(this.idleTimeout).isAfter(dayjs());

    return true;
  }

  get parsedXUAToken(): JWT | null {
    if (this.xuaToken) {
      const parts = this.xuaToken.split(".");
      if (parts.length === 3) {
        const decoded = base64UrlDecodeUnicode(parts[1]);
        return JSON.parse(decoded) as JWT;
      }
    }
    return null;
  }

  get loggedInUserGLN(): string | null {
    if (this.parsedXUAToken?.sub?.length === 13) {
      return this.parsedXUAToken.sub;
    }
    return null;
  }

  get currentUser(): IPerson | null {
    if (this.parsedIDToken?.name) {
      return {
        name: this.parsedIDToken.name,
      };
    } else if (this.parsedIDToken?.sub) {
      return {
        name: this.parsedIDToken.sub,
      };
    }
    return null;
  }

  get isPatientUser(): boolean {
    return (
      (this.role == Role.Patient || this.role == Role.Representative) &&
      this.loggedInUserRoles.includes(Role.Patient)
    );
  }

  get isProfessionalUser(): boolean {
    return this.role == Role.HealthcareProfessional || this.role == Role.Assistant;
  }

  get isDocumentAdministratorUser(): boolean {
    return this.role == Role.DocumentAdministrator;
  }

  get isPolicyAdministratorUser(): boolean {
    return this.role == Role.PolicyAdministrator;
  }

  get isPatientSelected(): boolean {
    return !!this.selectedPatient;
  }

  get isRepresentativeWithSelectedProxy(): boolean {
    return (
      this.role == Role.Representative &&
      this.loggedInUserRoles.includes(Role.Representative) &&
      this.proxyPerson != null
    );
  }

  get isPatientOrRepresentsPatient(): boolean {
    return this.isPatientUser || this.isRepresentativeWithSelectedProxy;
  }

  @Action({ rawError: true })
  async enableEmergencyAccess(verifiedToken: string) {
    this.setHasEmergencyAccess(false);
    this.setEmergencyAccessRequested(true);
    this.setCaptchaToken(verifiedToken);

    try {
      await this.getAuthenticationHeader();
      this.setHasEmergencyAccess(true);
      this.setEmergencyAccessRequested(false);
      this.clearCaptchaToken();
    } catch (e) {
      this.setHasEmergencyAccess(false);
      this.setEmergencyAccessRequested(false);
      this.clearCaptchaToken();
      throw e;
    }
  }

  @Action({ rawError: true })
  async disableEmergencyAccess() {
    this.setHasEmergencyAccess(false);
    this.setEmergencyAccessRequested(false);
    this.clearXUAToken();
    this.setNoAccessToThisEPD(false);
    try {
      await this.getAuthenticationHeader();
    } catch (e) {
      this.clearSelectedPatient();
      this.clearPatientSPID();
      throw e;
    }
  }

  // timer
  timer: number | null = null;
  remainingSessionTime: dayjs.Dayjs | null = null;

  get timerExpiresAt() {
    return this.idleTimeout || this.expiresAt;
  }

  get disableSessionContraints() {
    return config.session.disableSessionConstraints;
  }

  @Mutation
  setTimer(timer: number | null) {
    this.timer = timer;
  }

  @Mutation
  setRemainingSessionTime(remaining: dayjs.Dayjs | null) {
    this.remainingSessionTime = remaining;
  }

  @Action({ rawError: true })
  setupLogoutTimer() {
    if (this.timer) {
      return;
    }

    if (!this.disableSessionContraints && this.expiresAt) {
      const timer = () => {
        if (this.expiresAt) {
          const now = dayjs();
          const expirationDate = dayjs(this.timerExpiresAt);

          if (now.isAfter(expirationDate)) {
            return this.updateSessionTimeout();
          }

          const remaining = dayjs(expirationDate.diff(now));
          this.setRemainingSessionTime(remaining);
        }
      };

      timer();
      this.setTimer(window.setInterval(timer, 500));
    }
  }

  @Action
  clearTimer(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.setTimer(null);
    }
  }

  get principalId(): string | undefined {
    if (!this.proxyPerson || !this.parsedXUAToken) {
      return;
    }

    const xuaToken = this.parsedXUAToken as any;
    if (this.isProfessionalUser) {
      return xuaToken?.sub;
    }
    if (this.isPatientUser) {
      return xuaToken?.resourceID.split("^^^")[0];
    }
  }

  get principalName(): R4.IHumanName | undefined {
    if (!this.proxyPerson || !this.principalId) {
      return;
    }

    const selectedPrincipal = this.proxies?.find((principal) => {
      const principalId = (principal as any)?.gln || (principal as any)?.spid;
      return !!principalId && principalId === this.principalId;
    });

    const name: R4.IHumanName = {};

    if (selectedPrincipal) {
      name.family = selectedPrincipal.lastName;
      name.given = [selectedPrincipal.firstName];
    } else {
      const subjectName = (this.parsedXUAToken as any)?.SubjectID;
      if (!subjectName) {
        return;
      }
      name.text = (this.parsedXUAToken as any)?.SubjectID;
    }

    return name;
  }

  get userId(): string | undefined {
    const idToken = this.parsedIDToken as any | null;
    if (!idToken) {
      return;
    }
    return idToken.spid || idToken.gln || idToken.uid;
  }

  get userName(): R4.IHumanName | undefined {
    const givenName = this.parsedIDToken?.given_name;
    const familyName = this.parsedIDToken?.family_name;
    if (!givenName || !familyName) {
      return;
    }

    return {
      given: [givenName],
      family: familyName,
    };
  }

  get author(): R4.IPractitioner | R4.IPatient | R4.IRelatedPerson | undefined {
    if (!this.userName) {
      return;
    }

    if (this.isProfessionalUser) {
      return {
        resourceType: "Practitioner",
        id: crypto.randomUUID(),
        identifier: [
          {
            system: "urn:oid:2.51.1.3",
            value: this.userId,
          },
        ],
        name: [this.userName],
      } satisfies R4.IPractitioner;
    } else if (this.isRepresentativeWithSelectedProxy) {
      return {
        resourceType: "RelatedPerson",
        id: crypto.randomUUID(),
        identifier: [
          {
            system: `urn:oid:${config.assigningAuthorities.sts}`,
            value: this.userId,
          },
        ],
        name: [this.userName],
        patient: makeUuidUrnReference(this.patient),
      } satisfies R4.IRelatedPerson;
    } else {
      return {
        resourceType: "Patient",
        id: crypto.randomUUID(),
        identifier: [
          {
            system: "urn:oid:2.16.756.5.30.1.127.3.10.3",
            value: this.userId,
          },
        ],
        name: [this.userName],
      } satisfies R4.IPatient;
    }
  }

  get patientId(): string | undefined {
    return (this.parsedXUAToken as any)?.resourceID?.split("^^^")[0];
  }

  get patientIdentifier(): R4.IIdentifier | undefined {
    if (!this.patientId) {
      return;
    }

    return {
      system: "urn:oid:2.16.756.5.30.1.127.3.10.3",
      value: this.patientId,
    };
  }

  get patientName(): R4.IHumanName | null | undefined {
    // Professional, assistant, or admin after selecting a patient
    if (this.selectedPatient?.names) {
      return getPreferredName(this.selectedPatient.names);
    }
    // Representative
    if (this.proxyPerson && this.isPatientUser) {
      return this.principalName;
    }
    // Patient
    if (this.isPatientUser) {
      return this.userName;
    }
  }

  get patient(): R4.IPatient {
    const patient: R4.IPatient = {
      resourceType: "Patient",
      id: crypto.randomUUID(),
      meta: {
        profile: ["http://fhir.ch/ig/ch-core/StructureDefinition/ch-core-patient-epr"],
      },
      name: [],
      identifier: [],
      active: true,
    };

    if (this.patientIdentifier) {
      patient.identifier?.push(this.patientIdentifier);
    }

    if (this.patientName) {
      patient.name?.push(this.patientName);
    }

    return patient;
  }
}
